import { useEffect, useRef, useState } from "react";
import type { Quote } from "@/lib/types";

export interface LiveQuote {
  quote: Quote | null;
  connected: boolean;
}

/** Subscribes to the server's WebSocket quote stream with auto-reconnect. */
export function useLiveQuote(): LiveQuote {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [connected, setConnected] = useState(false);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let closed = false;
    let ws: WebSocket | null = null;
    let backoff = 1000;

    const connect = () => {
      const proto = location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${location.host}/ws`);

      ws.onopen = () => {
        setConnected(true);
        backoff = 1000;
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "quote" && msg.quote) setQuote(msg.quote);
        } catch {
          /* ignore malformed */
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closed) return;
        reconnectRef.current = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 15_000);
      };
      ws.onerror = () => ws?.close();
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectRef.current);
      ws?.close();
    };
  }, []);

  return { quote, connected };
}
